import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { Monitor } from '../../api';
import { useMonitorList } from '../../api';
import { TableV2 } from '../../components/TableV2';
import type { ExtendedDataTableColumn } from '../../components/TableV2/types';

import { FilterOptionType } from '@repo/common/components/Filter';
import { FILTER_OPTIONS_CONFIG } from '../../components/Filter/constants';
import {
	openBulkEditOwnersModal,
	useActions,
	useBulkAddTagsModal,
	useBulkEditSchedulesModal,
	useBulkEditThresholdsModal,
	useColumns,
} from './MonitorList.hooks';

const COLUMN_VISIBILITY = {
	catalogServerType: 'monitors',
	catalogType: 'monitor',
} as const;

const QUICK_ACTIONS = ['actions::run'] as const;

const PAGINATION_LIST_OPTIONS = {
	suspense: true,
};

const MONITOR_ADDITIONAL_FILTERS = [
	FilterOptionType.INTEGRATION,
	FilterOptionType.DATABASE,
	FilterOptionType.SCHEMA_NAME,
	FilterOptionType.OWNERS,
	FilterOptionType.MONITOR_METRIC_TYPE,
	FilterOptionType.MONITOR_TARGET,
	FilterOptionType.MONITOR_STATUS,
	FilterOptionType.INCIDENT_STATUS,
];

function MonitorList({
	withAdditionalButtons,
	defaultRequiredSearchParams,
	onSelectedRecordsStateChange,
}: {
	withAdditionalButtons?: React.ReactNode;
	defaultRequiredSearchParams?: Record<string, any>;
	onSelectedRecordsStateChange?: (args: { selectedRecords: any[] }) => void;
}) {
	const { openModal: openBulkEditModal, modal: bulkEditModal } =
		useBulkEditThresholdsModal();
	const { openModal: openBulkAddTagsModal, modal: bulkAddTagsModal } =
		useBulkAddTagsModal();
	const {
		openModal: openBulkEditSchedulesModal,
		modal: bulkEditSchedulesModal,
	} = useBulkEditSchedulesModal();
	const columns = useColumns();
	const actions = useActions(
		openBulkEditModal,
		openBulkAddTagsModal,
		openBulkEditSchedulesModal,
		openBulkEditOwnersModal
	);

	const navigate = useNavigate();

	const handleClick = useCallback(
		(id: string) => {
			navigate(`/monitor/${id}`);
		},
		[navigate]
	);

	const onCellClick = useCallback(
		(cell: {
			column: ExtendedDataTableColumn<Monitor>;
			record: { id: string };
		}) => {
			if (cell.column.navigate !== false) {
				handleClick(cell.record.id);
			}
		},
		[handleClick]
	);

	return (
		<>
			<TableV2
				pluralTypeString="monitors"
				tableCacheKey="monitors"
				columns={columns}
				columnVisibility={COLUMN_VISIBILITY}
				usePaginationList={useMonitorList}
				withActions={actions}
				withFilterOptions={MONITOR_ADDITIONAL_FILTERS.map(
					(filter) => FILTER_OPTIONS_CONFIG[filter]
				)}
				additionalFilters={MONITOR_ADDITIONAL_FILTERS}
				withCheckbox={true}
				withQuickActions={QUICK_ACTIONS}
				withCsvExport
				withInteractiveHeader
				withSearch
				defaultSort={null}
				withAdditionalButtons={withAdditionalButtons}
				defaultRequiredSearchParams={defaultRequiredSearchParams}
				onCellClick={onCellClick}
				usePaginationListOptions={PAGINATION_LIST_OPTIONS}
				onSelectedRecordsStateChange={onSelectedRecordsStateChange}
			/>
			{bulkEditModal}
			{bulkAddTagsModal}
			{bulkEditSchedulesModal}
		</>
	);
}

const MonitorListWithObserver = observer(MonitorList);
export default MonitorListWithObserver;
